import { PermissionBit, UserAccessPolicyResponse } from "../../../api";
import { AccessRules } from "../../../constants/roles";
import { DataModel } from "../../../models/data-model";
import { Location } from "./utils";

export type AccessPoliciesItem = UserAccessPolicyResponse & {
  username?: string;
  userRole?: string;
  numCountries?: number;
  numClinics?: number;
  numVisiblePatients?: number;
  countriesStr?: string;
  regionsStr?: string;
  locations?: Location[];
};

export class AccessPolicies extends DataModel<AccessPoliciesItem> {
  readonly idAttr = "uuid";

  constructor(
    data: AccessPoliciesItem[],
    permissions: PermissionBit[],
    userUUID: string,
  ) {
    super(data, permissions, userUUID);

    // access - only admin users can edit access policies
    this.access = {
      editRules: { medical: AccessRules.none, monitoring: AccessRules.none },
      deleteRules: { medical: AccessRules.none, monitoring: AccessRules.none },
      historyRequiresMonitoring: false,
    };
  }

  init() {
    this.data = this.data.map((item) => {
      // Calculate some useful display values
      const numCountries = item.countries?.length ?? 0;
      const numClinics = item.clinics?.length ?? 0;
      const numVisiblePatients = item.visible_patients?.length ?? 0;
      const username = item.connected_user?.username ?? "N/A";
      const userRole = item.connected_user?.role ?? "N/A";

      // Format countries as comma-separated string
      const countriesStr = item.countries?.join(", ") ?? "";

      // Format regions as readable string
      let regionsStr = "";
      if (item.regions) {
        const regionPairs = Object.entries(item.regions).map(
          ([country, regions]) => `${country}: ${regions.join(", ")}`,
        );
        regionsStr = regionPairs.join(" | ");
      }

      const locations = Object.entries(item.regions ?? {}).map(
        ([country, regions]): Location => ({
          country,
          regions,
        }),
      );

      return {
        ...item,
        username,
        userRole,
        numCountries,
        numClinics,
        numVisiblePatients,
        countriesStr,
        regionsStr,
        locations,
      };
    });
    super.init();
  }

  prepareDraft(item: AccessPoliciesItem) {
    const field = super.prepareDraft(item);

    // Copy permissions array (array of bit values)
    if (item.permissions) {
      field["permissions"] = [...item.permissions];
    }

    // Copy countries and regions for geographic editing
    if (item.countries) {
      field["countries"] = [...item.countries];
    }
    if (item.regions) {
      // Deep copy regions object
      field["regions"] = JSON.parse(JSON.stringify(item.regions));
    }
    if (item.locations) {
      // Deep copy regions object
      field["locations"] = [...item.locations];
    }

    // Convert related objects to IDs for editing
    if (item.clinics) {
      field["clinic_uuids"] = item.clinics.map((c) => c.uuid);
    }
    if (item.physicians) {
      field["physician_uuids"] = item.physicians.map((p) => p.uuid);
    }
    if (item.visible_patients) {
      field["visible_patient_ids"] = item.visible_patients.map(
        (p) => p.patient_id,
      );
    }

    return field;
  }
}
